/**
 * ImageDetailPopover — Hover popover for inline media images
 *
 * Shows filename, dimensions, type, transcript excerpt, conversation links,
 * and an "Open in Gallery" action. Rendered as a React portal positioned
 * relative to the hovered image.
 *
 * Data is fetched on first hover and cached for 5 minutes.
 *
 * @module @humanizer/studio/components/workspace/ImageDetailPopover
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMediaApi } from '../../hooks/useMediaApi';
import { extractPointerFromSrc } from '../../utils/media-url';
import type { MediaItem, TranscriptionVersion } from '../../hooks/useMediaApi';

// ═══════════════════════════════════════════════════════════════════════════
// CACHE
// ═══════════════════════════════════════════════════════════════════════════

interface CacheEntry {
  media: MediaItem | null;
  transcriptions: TranscriptionVersion[];
  associations: Array<{
    threadId: string;
    conversationTitle?: string;
    conversationDate?: string;
  }>;
  fetchedAt: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const cache = new Map<string, CacheEntry>();

function getCached(pointer: string): CacheEntry | null {
  const entry = cache.get(pointer);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > CACHE_TTL) {
    cache.delete(pointer);
    return null;
  }
  return entry;
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ImageDetailPopoverProps {
  imageSrc: string;
  imageRect: DOMRect;
  onOpenConversation: (threadId: string) => void;
  onOpenInGallery: () => void;
  onMouseEnterPopover: () => void;
  onMouseLeavePopover: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function ImageDetailPopover({
  imageSrc,
  imageRect,
  onOpenConversation,
  onOpenInGallery,
  onMouseEnterPopover,
  onMouseLeavePopover,
}: ImageDetailPopoverProps) {
  const mediaApi = useMediaApi();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<CacheEntry | null>(null);
  const [loading, setLoading] = useState(true);

  const pointer = extractPointerFromSrc(imageSrc);

  // Fetch data (or use cache)
  useEffect(() => {
    if (!pointer) {
      setLoading(false);
      return;
    }

    const cached = getCached(pointer);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const [mediaResult, contextResult] = await Promise.all([
          mediaApi.byPointer(pointer),
          window.electronArchive?.getMediaContext?.(pointer)
            .catch(() => ({ associations: [] }))
            ?? Promise.resolve({ associations: [] as Array<{
              nodeId: string; threadId: string; date?: string;
              authorRole?: string; snippet?: string;
              conversationTitle?: string; conversationDate?: string;
            }> }),
        ]);

        const media = mediaResult.found && mediaResult.media ? mediaResult.media : null;

        let transcriptions: TranscriptionVersion[] = [];
        if (media) {
          try {
            const tResult = await mediaApi.getTranscriptions(media.id);
            transcriptions = tResult.versions;
          } catch { /* non-fatal */ }
        }

        // Deduplicate associations by threadId
        const seen = new Set<string>();
        const associations = contextResult.associations.filter(a => {
          if (!a.threadId || seen.has(a.threadId)) return false;
          seen.add(a.threadId);
          return true;
        }).map(a => ({
          threadId: a.threadId,
          conversationTitle: a.conversationTitle,
          conversationDate: a.conversationDate,
        }));

        const entry: CacheEntry = {
          media,
          transcriptions,
          associations,
          fetchedAt: Date.now(),
        };
        cache.set(pointer, entry);

        if (!cancelled) {
          setData(entry);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [pointer, mediaApi]);

  // Position: below image if room, above if not
  const [position, setPosition] = useState<{ top: number; left: number; placement: 'below' | 'above' }>({
    top: 0, left: 0, placement: 'below',
  });

  useEffect(() => {
    const popoverHeight = 240; // estimated max
    const gap = 8;
    const spaceBelow = window.innerHeight - imageRect.bottom;
    const placement = spaceBelow >= popoverHeight + gap ? 'below' : 'above';
    const top = placement === 'below'
      ? imageRect.bottom + gap
      : imageRect.top - popoverHeight - gap;
    const left = Math.max(8, Math.min(
      imageRect.left + imageRect.width / 2 - 160,
      window.innerWidth - 328,
    ));
    setPosition({ top, left, placement });
  }, [imageRect]);

  // Get preferred transcription text
  const transcriptText = data?.transcriptions?.length
    ? (data.transcriptions.find(t => t.isPreferred)?.text
      ?? data.transcriptions.find(t => t.status === 'completed')?.text
      ?? '')
    : '';

  const media = data?.media;

  return createPortal(
    <div
      ref={popoverRef}
      className={`image-detail-popover image-detail-popover--${position.placement}`}
      style={{ top: position.top, left: position.left }}
      onMouseEnter={onMouseEnterPopover}
      onMouseLeave={onMouseLeavePopover}
    >
      {loading ? (
        <div className="image-detail-popover__loading">Loading...</div>
      ) : !media ? (
        <div className="image-detail-popover__empty">No media info available</div>
      ) : (
        <>
          {/* Header */}
          <div className="image-detail-popover__header">
            <span className="image-detail-popover__filename">
              {media.filename || 'Untitled'}
            </span>
          </div>

          {/* Meta */}
          <div className="image-detail-popover__meta">
            {media.mimeType && (
              <span className="image-detail-popover__tag">{media.mimeType}</span>
            )}
            {media.width && media.height && (
              <span className="image-detail-popover__tag">{media.width} x {media.height}</span>
            )}
            {media.size && (
              <span className="image-detail-popover__tag">
                {media.size > 1048576
                  ? `${(media.size / 1048576).toFixed(1)} MB`
                  : `${(media.size / 1024).toFixed(0)} KB`}
              </span>
            )}
          </div>

          {/* Transcript excerpt */}
          {transcriptText && (
            <div className="image-detail-popover__transcript">
              {transcriptText.length > 200
                ? transcriptText.slice(0, 200) + '...'
                : transcriptText}
            </div>
          )}

          {/* Description / caption */}
          {media.caption && !transcriptText && (
            <div className="image-detail-popover__transcript">
              {media.caption.length > 200
                ? media.caption.slice(0, 200) + '...'
                : media.caption}
            </div>
          )}

          {/* Conversation links */}
          {data.associations.length > 0 && (
            <div className="image-detail-popover__conversations">
              <span className="image-detail-popover__conversations-label">
                Referenced in:
              </span>
              {data.associations.slice(0, 3).map(a => (
                <button
                  key={a.threadId}
                  className="image-detail-popover__conversation-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenConversation(a.threadId);
                  }}
                >
                  {a.conversationTitle || 'Untitled conversation'}
                </button>
              ))}
              {data.associations.length > 3 && (
                <span className="image-detail-popover__more">
                  +{data.associations.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="image-detail-popover__actions">
            <button
              className="image-detail-popover__gallery-btn"
              onClick={(e) => {
                e.stopPropagation();
                onOpenInGallery();
              }}
            >
              Open in Gallery
            </button>
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
